// page-meetings-records-calendar.ts — Grille mensuelle de #meetings-records.
//
// Extrait de page-meetings-records.ts (cliquet fonction, août 2026), sur le
// modèle de page-meetings-records-rows.ts : rendu PUR, aucun état de closure —
// les réunions déjà filtrées, le mois affiché, la date du jour et le droit
// d'écriture arrivent en paramètres. Comportement identique à l'inline.
//
// Grille lundi→dimanche, de la semaine du 1er à celle du dernier jour du mois.
// Les réunions sont placées dans la cellule de leur jour, coloriées par TYPE DE
// COMITÉ. La navigation de mois reste dans la barre d'outils de la page, qu'elle
// partage avec les autres vues.
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t.

import { buildMonthGrid, committeeColor, dateKey } from '../domain/meeting-calendar.js';
import { calLinkPicto } from './page-meetings-records-rows.js';
import type { MeetingRecord } from './page-meetings-records.js';

type WeekdayKey = 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';
const WEEKDAY_KEYS: WeekdayKey[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function renderDayMeetings(dayMeetings: MeetingRecord[], canWrite: boolean): string {
  return dayMeetings
    .map((m) => {
      const c = committeeColor(m.committee_id);
      const tip = m.committee_code ? `${m.committee_code} — ${m.label}` : m.label;
      // CTAs (dupliquer / supprimer) réservés aux droits d'écriture ;
      // le glissé-déposer aussi (modifie la date de la réunion).
      const acts = canWrite
        ? `<span class="cal-meet-actions"><button type="button" class="cal-meet-btn" data-cal-dup="${m.id}" title="${esc(t('pageMeetingsRecords.calDuplicate'))}" aria-label="${esc(t('pageMeetingsRecords.calDuplicate'))}">⧉</button><button type="button" class="cal-meet-btn cal-meet-del" data-cal-del="${m.id}" title="${esc(t('common.delete'))}" aria-label="${esc(t('common.delete'))}">✕</button></span>`
        : '';
      const picto = m.from_calendar ? calLinkPicto() + ' ' : '';
      return `<div class="cal-meet"${canWrite ? ' draggable="true"' : ''} data-cal-meeting="${m.id}" style="background:${c.bg};color:${c.fg}" title="${esc(tip)}"><span class="cal-meet-title">${picto}${esc(m.label)}</span>${acts}</div>`;
    })
    .join('');
}

export function renderCalendarGrid(
  rows: readonly MeetingRecord[],
  year: number,
  month: number,
  today: Date,
  canWrite: boolean,
): string {
  // Regroupe les réunions filtrées par jour (clé YYYY-MM-DD).
  const byDate = new Map<string, MeetingRecord[]>();
  for (const m of rows) {
    if (!m.meeting_date) continue;
    const key = m.meeting_date.slice(0, 10);
    const arr = byDate.get(key);
    if (arr) arr.push(m);
    else byDate.set(key, [m]);
  }

  const cells = buildMonthGrid(year, month);
  // Clé du jour courant (même format que cell.key) pour la pastille « aujourd'hui ».
  const todayKey = dateKey(today);
  let body = '';
  cells.forEach((cell, i) => {
    if (i % 7 === 0) body += '<tr>';
    const lines = renderDayMeetings(byDate.get(cell.key) || [], canWrite);
    const addBtn = canWrite
      ? `<button type="button" class="cal-add" data-cal-add="${cell.key}" title="${esc(t('pageMeetingsRecords.calAdd'))}" aria-label="${esc(t('pageMeetingsRecords.calAdd'))}">+</button>`
      : '';
    const todayCls = cell.inMonth && cell.key === todayKey ? ' cal-today' : '';
    body += `<td class="cal-cell${cell.inMonth ? '' : ' cal-out'}" data-cal-date="${cell.key}"><div class="cal-dayhead"><span class="cal-daynum${todayCls}">${cell.day}</span>${addBtn}</div>${lines}</td>`;
    if (i % 7 === 6) body += '</tr>';
  });

  const head = WEEKDAY_KEYS.map(
    (k) => `<th>${esc(t('pageMeetingsRecords.weekday' + k))}</th>`,
  ).join('');

  return `
    <div class="card">
      <div class="table-wrap">
        <table class="cal-table">
          <thead><tr>${head}</tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>
    </div>`;
}

/** Libellé du mois affiché (« Août 2026 »), première lettre en capitale —
 *  `Intl` rend « août » en minuscule. Extrait de `renderMeetingsPage`
 *  (cliquet fonction, août 2026). */
export function monthLabel(year: number, month: number): string {
  const raw = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(
    new Date(year, month, 1),
  );
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}
